/**
 * World-class Calendar — day/week swimlanes + drag-reschedule + occupancy heatmap +
 * conflict-aware bar drag + next-available sidebar.
 *
 * Reads the composite snapshot from `/api/calendar/snapshot`. Bookings render
 * as colour-coded bars positioned within their resource row. HTML5 native
 * drag-and-drop fires `PATCH /api/calendar/bookings/:id/move-dates` on drop;
 * optimistic UI rolls back on 409.
 *
 * Mobile (<lg breakpoint) collapses to a per-day list rather than render the
 * Gantt — the swimlane layout is impractical on phones.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import {
  type CalendarBookingBar,
  type CalendarConflict,
  type CalendarOccupancyCell,
  type CalendarResource,
  type CalendarSnapshot,
  type CalendarView,
  type NextAvailableResult,
  checkCalendarConflicts,
  fetchCalendarSnapshot,
  fetchNextAvailable,
  moveBookingDates,
} from '@/lib/api';
import { useRealtime } from '@/lib/useRealtime';
import { useSpaceNoun } from '@/lib/useSpaceNoun';
import { addDays, format, startOfWeek } from 'date-fns';
import {
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Search,
  Sparkles,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const DAY_START_HOUR = 6;
const DAY_END_HOUR = 22;
const HOURS_PER_DAY = DAY_END_HOUR - DAY_START_HOUR;
const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

function ymd(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function categoryColors(cat: CalendarBookingBar['category']): {
  bg: string;
  border: string;
  text: string;
} {
  switch (cat) {
    case 'boarding':
      return { bg: 'bg-sky-500/15', border: 'border-sky-500/50', text: 'text-sky-900' };
    case 'daycare':
      return { bg: 'bg-emerald-500/15', border: 'border-emerald-500/50', text: 'text-emerald-900' };
    case 'grooming':
      return { bg: 'bg-purple-500/15', border: 'border-purple-500/50', text: 'text-purple-900' };
    default:
      return { bg: 'bg-orange-500/15', border: 'border-orange-500/50', text: 'text-orange-900' };
  }
}

function occupancyClass(pct: number): string {
  if (pct > 100) return 'bg-purple-500/70 text-white';
  if (pct > 85) return 'bg-red-500/70 text-white';
  if (pct >= 60) return 'bg-amber-400/70 text-amber-950';
  return 'bg-emerald-500/40 text-emerald-950';
}

export function Calendar() {
  const { state } = useAuthContext();
  const { me } = state;
  const facility = me?.activeFacility ?? null;
  const timezone = facility?.timezone ?? me?.activeAccount?.defaultTimezone ?? 'UTC';

  const [view, setView] = useState<CalendarView>('week');
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [snapshot, setSnapshot] = useState<CalendarSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSidebar, setShowSidebar] = useState(true);

  const range = useMemo(() => {
    if (view === 'day') {
      const d = ymd(anchor);
      return { from: d, to: d };
    }
    const start = startOfWeek(anchor, { weekStartsOn: 1 });
    return { from: ymd(start), to: ymd(addDays(start, 6)) };
  }, [view, anchor]);

  const refresh = useCallback(async () => {
    if (!facility) return;
    setLoading(true);
    setError(null);
    try {
      const snap = await fetchCalendarSnapshot({
        facilityId: facility.id,
        from: range.from,
        to: range.to,
        view,
      });
      setSnapshot(snap);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [facility, range.from, range.to, view]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useRealtime(
    useCallback(
      (event) => {
        if (!facility) return;
        if (
          event.type === 'BOOKING_CREATED' ||
          event.type === 'BOOKING_UPDATED' ||
          event.type === 'BOARDING_STAY_MOVED'
        ) {
          if ('facility_id' in event && event.facility_id === facility.id) {
            void refresh();
          }
        }
      },
      [facility, refresh],
    ),
  );

  if (!facility) {
    return (
      <div
        className="flex min-h-[50vh] flex-col items-center justify-center gap-2 p-8 text-center"
        data-testid="calendar-no-facility"
      >
        <p className="text-sm text-muted-foreground">Select a facility to view its calendar.</p>
      </div>
    );
  }

  const headerLabel =
    view === 'day'
      ? format(anchor, 'EEE d MMM yyyy')
      : `${format(startOfWeek(anchor, { weekStartsOn: 1 }), 'd MMM')} – ${format(
          addDays(startOfWeek(anchor, { weekStartsOn: 1 }), 6),
          'd MMM yyyy',
        )}`;

  function nudge(direction: -1 | 1) {
    const step = view === 'day' ? 1 : 7;
    setAnchor((d) => addDays(d, step * direction));
  }
  function jumpToToday() {
    setAnchor(new Date());
  }

  async function handleMove(
    booking: CalendarBookingBar,
    targetDateIso: string,
  ): Promise<{ ok: boolean; message?: string }> {
    if (!snapshot) return { ok: false };
    try {
      if (booking.bookingType === 'boarding') {
        const startMs = Date.parse(booking.startIso);
        const endMs = Date.parse(booking.endIso);
        const durationMs = endMs - startMs;
        // Preserve hour-of-day from existing checkIn.
        const oldHourMs = startMs % MS_PER_DAY;
        const targetMidnight = Date.parse(`${targetDateIso}T00:00:00.000Z`);
        const newStart = new Date(targetMidnight + oldHourMs);
        const newEnd = new Date(newStart.getTime() + durationMs);
        // Optimistic: update local state.
        setSnapshot((s) =>
          s
            ? {
                ...s,
                bookings: s.bookings.map((b) =>
                  b.bookingId === booking.bookingId
                    ? { ...b, startIso: newStart.toISOString(), endIso: newEnd.toISOString() }
                    : b,
                ),
              }
            : s,
        );
        await moveBookingDates(booking.bookingId, {
          newCheckIn: newStart.toISOString(),
          newCheckout: newEnd.toISOString(),
        });
        return { ok: true };
      }
      if (booking.bookingType === 'grooming') {
        const oldHourMs = Date.parse(booking.startIso) % MS_PER_DAY;
        const targetMidnight = Date.parse(`${targetDateIso}T00:00:00.000Z`);
        const newStart = new Date(targetMidnight + oldHourMs);
        setSnapshot((s) =>
          s
            ? {
                ...s,
                bookings: s.bookings.map((b) =>
                  b.bookingId === booking.bookingId
                    ? {
                        ...b,
                        startIso: newStart.toISOString(),
                        endIso: new Date(
                          newStart.getTime() + (Date.parse(b.endIso) - Date.parse(b.startIso)),
                        ).toISOString(),
                      }
                    : b,
                ),
              }
            : s,
        );
        await moveBookingDates(booking.bookingId, {
          newStartAt: newStart.toISOString(),
        });
        return { ok: true };
      }
      return { ok: false, message: 'unsupported_type' };
    } catch (e) {
      // Rollback by refetching.
      await refresh();
      return { ok: false, message: e instanceof Error ? e.message : 'move_failed' };
    }
  }

  async function handleResizeDates(
    bookingId: string,
    newCheckIn: string,
    newCheckout: string,
  ): Promise<{ ok: boolean; message?: string }> {
    if (!snapshot) return { ok: false };
    try {
      // Optimistic update.
      setSnapshot((s) =>
        s
          ? {
              ...s,
              bookings: s.bookings.map((b) =>
                b.bookingId === bookingId ? { ...b, startIso: newCheckIn, endIso: newCheckout } : b,
              ),
            }
          : s,
      );
      await moveBookingDates(bookingId, { newCheckIn, newCheckout });
      return { ok: true };
    } catch (e) {
      await refresh();
      return { ok: false, message: e instanceof Error ? e.message : 'resize_failed' };
    }
  }

  return (
    <div className="flex min-h-[100dvh] flex-col" data-testid="calendar-page">
      {/* Header */}
      {/* Phones: title on its own line, then [Day|Week] … [Today], then the
          prev/label/next row full-width. md+: one row. The sidebar toggle only
          appears at lg+ because the next-available aside it controls is lg-only. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-3 sm:px-6 sm:py-4">
        <h1 className="w-full min-w-0 text-base font-semibold tracking-tight md:w-auto md:flex-1">
          Calendar
          <span className="ml-2 text-sm font-normal text-muted-foreground">{facility.name}</span>
        </h1>

        <div className="flex items-center gap-1 rounded-lg border border-border p-0.5 md:p-1">
          <button
            type="button"
            onClick={() => setView('day')}
            className={`min-h-11 rounded-md px-3 py-1 text-xs font-medium press md:min-h-0 ${
              view === 'day' ? 'bg-secondary text-foreground' : 'text-muted-foreground'
            }`}
            data-testid="calendar-view-day"
          >
            Day
          </button>
          <button
            type="button"
            onClick={() => setView('week')}
            className={`min-h-11 rounded-md px-3 py-1 text-xs font-medium press md:min-h-0 ${
              view === 'week' ? 'bg-secondary text-foreground' : 'text-muted-foreground'
            }`}
            data-testid="calendar-view-week"
          >
            Week
          </button>
        </div>

        <div className="order-last flex w-full items-center justify-between gap-1 md:order-none md:w-auto md:justify-start">
          <button
            type="button"
            onClick={() => nudge(-1)}
            aria-label="Previous"
            className="grid size-11 place-items-center rounded-lg text-muted-foreground hover:bg-secondary press md:size-8"
          >
            <ChevronLeft className="size-4" strokeWidth={1.75} />
          </button>
          <span className="num min-w-0 flex-1 text-center text-sm font-medium md:min-w-[200px] md:flex-none">
            {headerLabel}
          </span>
          <button
            type="button"
            onClick={() => nudge(1)}
            aria-label="Next"
            className="grid size-11 place-items-center rounded-lg text-muted-foreground hover:bg-secondary press md:size-8"
          >
            <ChevronRight className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        <button
          type="button"
          onClick={jumpToToday}
          className="min-h-11 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-secondary press md:min-h-0"
        >
          Today
        </button>

        <button
          type="button"
          onClick={() => setShowSidebar((v) => !v)}
          className="ml-auto hidden min-h-11 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-secondary press md:ml-0 md:min-h-0 lg:inline-block"
          data-testid="calendar-sidebar-toggle"
        >
          <span className="inline-flex items-center gap-1.5">
            <Sparkles className="size-4" strokeWidth={1.75} />
            {showSidebar ? 'Hide' : 'Find slot'}
          </span>
        </button>
      </div>

      {error && (
        <div className="mx-4 mt-4 rounded-lg border border-destructive/20 sm:mx-6 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid flex-1 grid-cols-1 lg:grid-cols-[1fr_auto]">
        <div className="min-w-0 px-4 py-4 sm:px-6">
          {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

          {snapshot && (
            <>
              <OccupancyHeatmap
                occupancy={snapshot.occupancy}
                onPick={(date) => {
                  setView('day');
                  setAnchor(new Date(`${date}T12:00:00.000Z`));
                }}
              />

              {/* Mobile fallback — list view per-date */}
              <div className="lg:hidden mt-4 space-y-4" data-testid="calendar-mobile-list">
                {snapshot.occupancy.map((cell) => (
                  <MobileDayList
                    key={cell.date}
                    date={cell.date}
                    bookings={snapshot.bookings.filter((b) =>
                      overlapsDate(b.startIso, b.endIso, cell.date),
                    )}
                    timezone={timezone}
                  />
                ))}
              </div>

              {/* Desktop Gantt swimlanes */}
              <div className="hidden lg:block mt-4" data-testid="calendar-gantt">
                <GanttGrid
                  snapshot={snapshot}
                  view={view}
                  anchor={anchor}
                  onMove={handleMove}
                  onResizeDates={handleResizeDates}
                />
              </div>
            </>
          )}
        </div>

        {showSidebar && (
          <aside
            className="hidden lg:block w-80 border-l border-border bg-card/40 p-4"
            data-testid="calendar-sidebar"
          >
            <NextAvailablePanel facilityId={facility.id} />
          </aside>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Occupancy heatmap row
// ---------------------------------------------------------------------------
function OccupancyHeatmap({
  occupancy,
  onPick,
}: {
  occupancy: CalendarOccupancyCell[];
  onPick: (date: string) => void;
}) {
  return (
    <div
      className="-mx-4 flex snap-x items-stretch gap-1.5 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6 lg:mx-0 lg:overflow-visible lg:px-0 lg:pb-0"
      role="list"
      aria-label="Daily occupancy"
      data-testid="occupancy-heatmap"
    >
      {occupancy.map((cell) => (
        <button
          key={cell.date}
          type="button"
          onClick={() => onPick(cell.date)}
          className={`min-h-11 min-w-[4.5rem] flex-1 shrink-0 snap-start whitespace-nowrap rounded-md border border-border/60 px-2 py-2 text-left press lg:min-w-0 lg:shrink ${occupancyClass(cell.pct)}`}
          title={`${cell.used} of ${cell.capacity} used (${cell.pct}%)`}
          data-testid={`occupancy-cell-${cell.date}`}
        >
          <div className="text-xs font-medium opacity-90">
            {format(new Date(cell.date), 'EEE d')}
          </div>
          <div className="num mt-0.5 text-sm font-semibold">{cell.pct}%</div>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gantt grid — swimlanes per resource, columns are dates (week) or hours (day)
// ---------------------------------------------------------------------------
interface GanttGridProps {
  snapshot: CalendarSnapshot;
  view: CalendarView;
  anchor: Date;
  onMove: (
    booking: CalendarBookingBar,
    targetDateIso: string,
  ) => Promise<{ ok: boolean; message?: string }>;
  onResizeDates: (
    bookingId: string,
    newCheckIn: string,
    newCheckout: string,
  ) => Promise<{ ok: boolean; message?: string }>;
}

function GanttGrid({ snapshot, view, anchor, onMove, onResizeDates }: GanttGridProps) {
  const dates = useMemo(() => {
    if (view === 'day') return [ymd(anchor)];
    const start = startOfWeek(anchor, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => ymd(addDays(start, i)));
  }, [view, anchor]);

  const columnCount = view === 'day' ? HOURS_PER_DAY : dates.length;
  const headerLabels = useMemo(() => {
    if (view === 'day') {
      return Array.from(
        { length: HOURS_PER_DAY },
        (_, i) => `${String(DAY_START_HOUR + i).padStart(2, '0')}:00`,
      );
    }
    return dates.map((d) => format(new Date(d), 'EEE d'));
  }, [view, dates]);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropError, setDropError] = useState<string | null>(null);

  const handleResize = useCallback(
    async (bookingId: string, edge: 'start' | 'end', newDateIso: string) => {
      const booking = snapshot.bookings.find((b) => b.bookingId === bookingId);
      if (!booking || booking.bookingType !== 'boarding') return;

      const currentStartDate = booking.startIso.slice(0, 10);
      const currentEndDate = booking.endIso.slice(0, 10);
      const origStartTime = booking.startIso.slice(11); // preserve HH:mm:ss...
      const origEndTime = booking.endIso.slice(11);

      const newCheckIn = edge === 'start' ? newDateIso : currentStartDate;
      const newCheckout = edge === 'end' ? newDateIso : currentEndDate;
      if (newCheckIn >= newCheckout) return; // Prevent zero/negative span

      const result = await onResizeDates(
        bookingId,
        `${newCheckIn}T${origStartTime}`,
        `${newCheckout}T${origEndTime}`,
      );
      if (!result.ok) {
        setDropError(result.message ?? 'Could not resize booking');
      }
    },
    [snapshot.bookings, onResizeDates],
  );

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-card">
      {/* Header row */}
      <div
        className="grid border-b border-border bg-secondary/40 text-xs font-medium text-muted-foreground"
        style={{ gridTemplateColumns: `200px repeat(${columnCount}, minmax(0, 1fr))` }}
      >
        <div className="border-r border-border px-3 py-2">Resource</div>
        {headerLabels.map((label, i) => (
          <div
            key={`${label}-${i}`}
            className="border-r border-border px-2 py-2 text-center last:border-r-0"
          >
            {label}
          </div>
        ))}
      </div>

      {/* Swimlanes */}
      {snapshot.resources.length === 0 && (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          No runs or groomers configured for this facility.
        </div>
      )}
      {snapshot.resources.map((resource) => (
        <Swimlane
          key={resource.id}
          resource={resource}
          dates={dates}
          view={view}
          bookings={snapshot.bookings.filter((b) => b.resourceId === resource.id)}
          isDraggingId={draggingId}
          onDragStart={(id) => setDraggingId(id)}
          onDragEnd={() => setDraggingId(null)}
          onDrop={async (bookingId, targetDate) => {
            const booking = snapshot.bookings.find((b) => b.bookingId === bookingId);
            if (!booking) return;
            setDropError(null);
            const result = await onMove(booking, targetDate);
            if (!result.ok) {
              setDropError(result.message ?? 'Could not move booking');
            }
          }}
          onResize={handleResize}
        />
      ))}

      {/* Unassigned daycare + unassigned grooming — only when bookings exist without a resource */}
      {snapshot.bookings.some((b) => b.resourceId === null) && (
        <Swimlane
          key="__unassigned"
          resource={{
            id: '__unassigned',
            type: 'run',
            name: 'Unassigned',
            facilityId: null,
            capacityMax: 0,
          }}
          dates={dates}
          view={view}
          bookings={snapshot.bookings.filter((b) => b.resourceId === null)}
          isDraggingId={draggingId}
          onDragStart={(id) => setDraggingId(id)}
          onDragEnd={() => setDraggingId(null)}
          onDrop={() => {
            /* unassigned lane is read-only — drag bars OUT, not in */
          }}
        />
      )}

      {dropError && (
        <div className="border-t border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {dropError}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grouped bar — merges individual booking bars that share a bookingGroupId
// into a single visual bar showing all pet names.
// ---------------------------------------------------------------------------
interface GroupedBar {
  /** The representative bookingId used for drag-and-drop operations. */
  bookingId: string;
  bookingGroupId: string | null;
  bookingType: CalendarBookingBar['bookingType'];
  status: string;
  category: CalendarBookingBar['category'];
  resourceId: string | null;
  customerId: string;
  customerName: string;
  petNames: string[]; // may be multiple for a group booking
  startIso: string;
  endIso: string;
  /** True if any booking in the group is an overstay (checked_in past checkout). */
  isOverstay: boolean;
}

/**
 * Collapse individual bars into grouped bars.
 * Bars that share a bookingGroupId are merged:
 *   - petNames accumulates all pet names
 *   - startIso is the earliest; endIso is the latest across the group
 *   - bookingId is the first in the group (used for drag-drop + data-testid)
 */
function groupBars(bars: CalendarBookingBar[]): GroupedBar[] {
  const map = new Map<string, GroupedBar>();
  const out: GroupedBar[] = [];
  for (const b of bars) {
    const key = b.bookingGroupId ?? b.bookingId;
    const existing = map.get(key);
    if (existing) {
      if (!existing.petNames.includes(b.petName)) {
        existing.petNames.push(b.petName);
      }
      if (b.startIso < existing.startIso) existing.startIso = b.startIso;
      if (b.endIso > existing.endIso) existing.endIso = b.endIso;
      if (b.isOverstay) existing.isOverstay = true;
    } else {
      const g: GroupedBar = {
        bookingId: b.bookingId,
        bookingGroupId: b.bookingGroupId,
        bookingType: b.bookingType,
        status: b.status,
        category: b.category,
        resourceId: b.resourceId,
        customerId: b.customerId,
        customerName: b.customerName,
        petNames: [b.petName],
        startIso: b.startIso,
        endIso: b.endIso,
        isOverstay: b.isOverstay ?? false,
      };
      map.set(key, g);
      out.push(g);
    }
  }
  return out;
}

const LANE_HEIGHT_PX = 40;

/**
 * Assign each bar to a horizontal lane within its resource row so that
 * overlapping bars don't paint on top of each other.
 *
 * Two bars "overlap" when their column spans intersect. Returns a Map from
 * bookingId to lane index (0-based).
 */
function assignLanes(bars: GroupedBar[], dates: string[], view: CalendarView): Map<string, number> {
  const positions = bars
    .map((b) => ({ id: b.bookingId, pos: computeBarPosition(b, dates, view) }))
    .filter((x): x is { id: string; pos: { startCol: number; endCol: number } } => x.pos !== null);

  const laneMap = new Map<string, number>();
  // laneEnds[lane] = the endCol of the last bar in that lane
  const laneEnds: number[] = [];

  // Sort by startCol so earlier bars get lower lanes.
  positions.sort((a, b) => a.pos.startCol - b.pos.startCol);

  for (const { id, pos } of positions) {
    // Find the first lane whose last bar ends at or before this bar's start.
    let placed = false;
    for (let lane = 0; lane < laneEnds.length; lane++) {
      if ((laneEnds[lane] ?? 0) <= pos.startCol) {
        laneMap.set(id, lane);
        laneEnds[lane] = pos.endCol;
        placed = true;
        break;
      }
    }
    if (!placed) {
      const lane = laneEnds.length;
      laneMap.set(id, lane);
      laneEnds.push(pos.endCol);
    }
  }
  return laneMap;
}

function Swimlane({
  resource,
  dates,
  view,
  bookings,
  isDraggingId,
  onDragStart,
  onDragEnd,
  onDrop,
  onResize,
}: {
  resource: CalendarResource;
  dates: string[];
  view: CalendarView;
  bookings: CalendarBookingBar[];
  isDraggingId: string | null;
  onDragStart: (bookingId: string) => void;
  onDragEnd: () => void;
  onDrop: (bookingId: string, targetDate: string) => void;
  onResize?: (bookingId: string, edge: 'start' | 'end', newDateIso: string) => Promise<void>;
}) {
  const noun = useSpaceNoun();
  const columnCount = view === 'day' ? HOURS_PER_DAY : dates.length;

  // Ref to the grid container so we can compute column positions from pointer X.
  const gridContainerRef = useRef<HTMLDivElement>(null);
  // Active resize state: which bar + which edge + preview column.
  const [resizing, setResizing] = useState<{
    bookingId: string;
    edge: 'start' | 'end';
    previewCol: number;
    originalStart: string;
    originalEnd: string;
  } | null>(null);

  // Convert a page-X coordinate to a column index (0-based within dates/hours).
  const pageXToCol = useCallback(
    (pageX: number): number => {
      if (!gridContainerRef.current) return 0;
      const rect = gridContainerRef.current.getBoundingClientRect();
      const LABEL_PX = 200;
      const gridWidth = rect.width - LABEL_PX;
      const relX = pageX - rect.left - LABEL_PX;
      const col = Math.floor((relX / gridWidth) * columnCount);
      return Math.max(0, Math.min(columnCount - 1, col));
    },
    [columnCount],
  );

  const handleResizePointerDown = useCallback(
    (
      e: React.PointerEvent<HTMLButtonElement>,
      bar: GroupedBar,
      edge: 'start' | 'end',
      pos: { startCol: number; endCol: number },
    ) => {
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      setResizing({
        bookingId: bar.bookingId,
        edge,
        previewCol: edge === 'start' ? pos.startCol : pos.endCol - 1,
        originalStart: bar.startIso,
        originalEnd: bar.endIso,
      });
    },
    [],
  );

  const handleResizePointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!resizing) return;
      const col = pageXToCol(e.pageX);
      setResizing((prev) => (prev ? { ...prev, previewCol: col } : null));
    },
    [resizing, pageXToCol],
  );

  const handleResizePointerUp = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!resizing || !onResize) {
        setResizing(null);
        return;
      }
      const col = pageXToCol(e.pageX);
      const dateAtCol = view === 'week' ? dates[col] : null;
      if (dateAtCol) {
        void onResize(resizing.bookingId, resizing.edge, dateAtCol);
      }
      setResizing(null);
    },
    [resizing, onResize, pageXToCol, view, dates],
  );

  // Bug 1 fix: merge same-group bookings into a single visual bar.
  const groupedBars = groupBars(bookings);

  // Bug 2 fix: assign each bar to a vertical lane so overlapping bars don't
  // paint on top of each other.
  const laneMap = assignLanes(groupedBars, dates, view);
  const laneCount = Math.max(1, laneMap.size > 0 ? Math.max(...laneMap.values()) + 1 : 1);
  const rowHeightPx = laneCount * LANE_HEIGHT_PX;

  return (
    <div
      ref={gridContainerRef}
      className="grid border-b border-border last:border-b-0"
      style={{ gridTemplateColumns: `200px repeat(${columnCount}, minmax(0, 1fr))` }}
      data-testid={`swimlane-${resource.id}`}
    >
      {/* Label cell */}
      <div
        className="border-r border-border px-3 py-2 text-sm"
        style={{ minHeight: `${rowHeightPx}px` }}
      >
        <div className="font-medium">{resource.name}</div>
        <div className="text-xs text-muted-foreground">
          {resource.type === 'run' ? noun.Singular : resource.type === 'groomer' ? 'Groomer' : ''}
        </div>
      </div>

      {/* Drop-target cells — one per column (date or hour) */}
      {(view === 'day'
        ? Array.from({ length: HOURS_PER_DAY }, (_, i) => i)
        : dates.map((_, i) => i)
      ).map((i) => {
        const date = view === 'day' ? dates[0] : dates[i];
        if (!date) return null;
        return (
          <div
            key={`${resource.id}-col-${i}`}
            className="relative border-r border-border/40 last:border-r-0"
            style={{ minHeight: `${rowHeightPx}px` }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e) => {
              e.preventDefault();
              const bookingId = e.dataTransfer.getData('text/booking-id');
              if (bookingId) onDrop(bookingId, date);
            }}
            data-testid={`cell-${resource.id}-${date}-${i}`}
          />
        );
      })}

      {/* Booking bars — positioned via gridColumn + absolute top per lane */}
      {groupedBars.map((b) => {
        const pos = computeBarPosition(b, dates, view);
        if (!pos) return null;
        const lane = laneMap.get(b.bookingId) ?? 0;
        const colors = categoryColors(b.category);
        const label = b.petNames.join(', ');
        const isDragging = isDraggingId === b.bookingId;
        const overstay = b.isOverstay === true;
        // During resize, override the visual start/end col.
        const isResizingThis = resizing?.bookingId === b.bookingId;
        const visStartCol =
          isResizingThis && resizing?.edge === 'start' ? resizing.previewCol : pos.startCol;
        const visEndCol =
          isResizingThis && resizing?.edge === 'end' ? resizing.previewCol + 1 : pos.endCol;

        // Overstay = a checked_in stay past its scheduled checkout. Surface it
        // with an amber tone, a diagonal-stripe (hatched) fill, and an explicit
        // text badge so the indicator is more than colour alone (accessibility).
        const overstayTone = overstay ? 'border-amber-500 text-amber-950' : '';
        const overstayHatch: React.CSSProperties = overstay
          ? {
              backgroundColor: 'oklch(0.92 0.09 85)',
              backgroundImage:
                'repeating-linear-gradient(45deg, oklch(0.86 0.13 80 / 0.55) 0, oklch(0.86 0.13 80 / 0.55) 6px, transparent 6px, transparent 12px)',
            }
          : {};

        return (
          <div
            key={b.bookingId}
            className={`group/bar z-10 mx-0.5 rounded-md border text-xs shadow-sm hover:shadow-md ${overstay ? overstayTone : `${colors.bg} ${colors.border} ${colors.text}`} ${isDragging || isResizingThis ? 'opacity-70' : ''}`}
            data-overstay={overstay ? 'true' : undefined}
            style={{
              gridColumn: `${visStartCol + 2} / ${visEndCol + 2}`,
              gridRow: 1,
              position: 'relative',
              top: `${lane * LANE_HEIGHT_PX + 4}px`,
              height: `${LANE_HEIGHT_PX - 8}px`,
              display: 'flex',
              alignItems: 'stretch',
              ...overstayHatch,
            }}
          >
            {/* Left resize handle — only in week view where dates are columns */}
            {view === 'week' && onResize && (
              <button
                type="button"
                className="flex w-2 shrink-0 cursor-ew-resize items-center justify-center opacity-0 hover:opacity-100 group-hover/bar:opacity-60 rounded-l-md"
                title="Drag to change check-in date"
                onPointerDown={(e) => handleResizePointerDown(e, b, 'start', pos)}
                onPointerMove={handleResizePointerMove}
                onPointerUp={handleResizePointerUp}
              >
                <span className="w-0.5 h-3 rounded-full bg-current opacity-60" />
              </button>
            )}

            {/* Main clickable area — navigates to booking detail */}
            <Link
              to={`/bookings/${b.bookingGroupId ?? b.bookingId}`}
              draggable
              data-testid={`booking-bar-${b.bookingId}`}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/booking-id', b.bookingId);
                e.dataTransfer.effectAllowed = 'move';
                onDragStart(b.bookingId);
              }}
              onDragEnd={() => onDragEnd()}
              className="min-w-0 flex-1 cursor-grab truncate px-1 py-0.5 active:cursor-grabbing"
              title={
                overstay
                  ? `Overstay — pet has not checked out · ${label} — ${b.customerName}`
                  : `${label} — ${b.customerName}`
              }
            >
              <div className="flex items-center gap-1 truncate font-medium leading-tight">
                {overstay && (
                  <span
                    className="inline-flex shrink-0 items-center gap-0.5 rounded-sm bg-amber-500 px-1 text-[9px] font-semibold uppercase tracking-wide text-amber-950"
                    data-testid={`overstay-badge-${b.bookingId}`}
                  >
                    <AlertTriangle className="size-2.5" strokeWidth={2} aria-hidden="true" />
                    Overstay
                  </span>
                )}
                <span className="truncate">{label}</span>
              </div>
              <div className="truncate text-[10px] opacity-70 leading-tight">{b.customerName}</div>
            </Link>

            {/* Right resize handle — only in week view */}
            {view === 'week' && onResize && (
              <button
                type="button"
                className="flex w-2 shrink-0 cursor-ew-resize items-center justify-center opacity-0 hover:opacity-100 group-hover/bar:opacity-60 rounded-r-md"
                title="Drag to change checkout date"
                onPointerDown={(e) => handleResizePointerDown(e, b, 'end', pos)}
                onPointerMove={handleResizePointerMove}
                onPointerUp={handleResizePointerUp}
              >
                <span className="w-0.5 h-3 rounded-full bg-current opacity-60" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Compute the [startCol, endCol) range a booking spans within the swimlane.
 * In week view columns are dates; in day view columns are hours within DAY_START_HOUR..DAY_END_HOUR.
 * Returns null if the booking falls outside the visible range.
 *
 * Accepts any object with `startIso` + `endIso` (works for both CalendarBookingBar
 * and the GroupedBar type).
 */
function computeBarPosition(
  b: { startIso: string; endIso: string },
  dates: string[],
  view: CalendarView,
): { startCol: number; endCol: number } | null {
  if (view === 'week') {
    const start = b.startIso.slice(0, 10);
    const end = b.endIso.slice(0, 10);
    const startCol = dates.indexOf(start);
    const endCol = dates.indexOf(end);
    if (startCol === -1 && endCol === -1) {
      // Stay completely covers the visible range — render full width.
      if (
        b.startIso < `${dates[0]}T00:00:00Z` &&
        b.endIso > `${dates[dates.length - 1]}T23:59:59Z`
      ) {
        return { startCol: 0, endCol: dates.length };
      }
      return null;
    }
    return {
      startCol: Math.max(0, startCol),
      endCol: Math.min(dates.length, (endCol === -1 ? dates.length - 1 : endCol) + 1),
    };
  }
  // Day view: columns are hours.
  const d = dates[0];
  if (!d) return null;
  if (b.startIso.slice(0, 10) > d || b.endIso.slice(0, 10) < d) return null;
  const startMs = Math.max(
    Date.parse(b.startIso),
    Date.parse(`${d}T${String(DAY_START_HOUR).padStart(2, '0')}:00:00.000Z`),
  );
  const endMs = Math.min(
    Date.parse(b.endIso),
    Date.parse(`${d}T${String(DAY_END_HOUR).padStart(2, '0')}:00:00.000Z`),
  );
  if (endMs <= startMs) return null;
  const startHourOffset =
    (startMs - Date.parse(`${d}T${String(DAY_START_HOUR).padStart(2, '0')}:00:00.000Z`)) /
    MS_PER_HOUR;
  const endHourOffset =
    (endMs - Date.parse(`${d}T${String(DAY_START_HOUR).padStart(2, '0')}:00:00.000Z`)) /
    MS_PER_HOUR;
  return {
    startCol: Math.max(0, Math.floor(startHourOffset)),
    endCol: Math.min(HOURS_PER_DAY, Math.ceil(endHourOffset)),
  };
}

// ---------------------------------------------------------------------------
// Mobile per-day list fallback
// ---------------------------------------------------------------------------
function overlapsDate(startIso: string, endIso: string, date: string): boolean {
  return startIso.slice(0, 10) <= date && endIso.slice(0, 10) >= date;
}

/** Whole days between two YYYY-MM-DD strings (b - a). */
function daysBetween(a: string, b: string): number {
  const ms = Date.parse(`${b}T00:00:00.000Z`) - Date.parse(`${a}T00:00:00.000Z`);
  return Number.isFinite(ms) ? Math.round(ms / MS_PER_DAY) : 0;
}

/**
 * Right-hand label for a booking row in the per-day mobile list.
 *
 * Only grooming appointments carry a genuine clock time (`grooming_appointments.start_at`).
 * Boarding `startIso`/`endIso` are date-derived (check-in / checkout day rendered as
 * midnight UTC — "01:00" in BST), and the API synthesises a 9–5 window for daycare
 * visits, so neither is shown as a time. Boarding shows where `date` falls in the
 * stay instead; daycare shows the category.
 */
export function mobileBookingLabel(
  b: Pick<CalendarBookingBar, 'category' | 'startIso' | 'endIso' | 'isOverstay'>,
  date: string,
  timezone: string,
): string {
  if (b.category === 'grooming') {
    try {
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(b.startIso));
    } catch {
      return b.startIso.slice(11, 16);
    }
  }
  if (b.category === 'daycare') return 'Daycare';
  const start = b.startIso.slice(0, 10);
  const end = b.endIso.slice(0, 10);
  if (start === date && end === date) return 'In & out';
  if (start === date) return 'Check-in';
  if (end === date) return b.isOverstay ? 'Overstay' : 'Check-out';
  const nights = daysBetween(start, end);
  const night = daysBetween(start, date);
  if (nights > 0 && night > 0 && night <= nights) return `Staying · night ${night} of ${nights}`;
  return 'Staying';
}

export function MobileDayList({
  date,
  bookings,
  timezone,
}: {
  date: string;
  bookings: CalendarBookingBar[];
  timezone: string;
}) {
  return (
    <div data-testid={`mobile-day-${date}`}>
      <div className="text-sm font-medium text-muted-foreground">
        <CalendarDays className="inline size-4 mr-1" strokeWidth={1.75} />
        {format(new Date(date), 'EEE d MMM')}
      </div>
      <div className="mt-1 flex flex-col gap-1">
        {bookings.length === 0 && (
          <div className="rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
            No bookings
          </div>
        )}
        {bookings.map((b) => {
          const colors = categoryColors(b.category);
          const overstay = b.isOverstay === true;
          return (
            <Link
              key={b.bookingId}
              to={`/bookings/${b.bookingGroupId ?? b.bookingId}`}
              className={`flex min-h-11 items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm ${
                overstay
                  ? 'border-amber-500 bg-amber-100 text-amber-950'
                  : `${colors.bg} ${colors.border} ${colors.text}`
              }`}
              title={overstay ? 'Overstay — pet has not checked out' : undefined}
            >
              <span className="flex min-w-0 items-center gap-1.5 font-medium">
                {overstay && (
                  <span className="inline-flex items-center gap-0.5 rounded-sm bg-amber-500 px-1 text-[9px] font-semibold uppercase tracking-wide text-amber-950">
                    <AlertTriangle className="size-2.5" strokeWidth={2} aria-hidden="true" />
                    Overstay
                  </span>
                )}
                <span className="truncate">{b.petName}</span>
              </span>
              <span className="num shrink-0 text-xs opacity-70">
                {mobileBookingLabel(b, date, timezone)}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Next-available sidebar
// ---------------------------------------------------------------------------
function NextAvailablePanel({ facilityId }: { facilityId: string }) {
  const [category, setCategory] = useState<'boarding' | 'daycare' | 'grooming'>('boarding');
  const [duration, setDuration] = useState(1440); // 24h = 1 night default
  const [fromDate, setFromDate] = useState(ymd(new Date()));
  const [result, setResult] = useState<NextAvailableResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  async function find() {
    setBusy(true);
    setSearchError(null);
    try {
      const r = await fetchNextAvailable({
        facilityId,
        category,
        from: fromDate,
        duration,
      });
      setResult(r);
    } catch (e) {
      setSearchError(e instanceof Error ? e.message : 'Search failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-3" data-testid="next-available-panel">
      <h3 className="text-sm font-semibold">Find next available slot</h3>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        Category
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as typeof category)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          data-testid="next-available-category"
        >
          <option value="boarding">Boarding</option>
          <option value="daycare">Daycare</option>
          <option value="grooming">Grooming</option>
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        From date
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        />
      </label>
      {category === 'grooming' ? (
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Duration (min)
          <input
            type="number"
            min={15}
            step={15}
            value={duration}
            onChange={(e) => setDuration(Math.max(15, Number(e.target.value) || 15))}
            className="num rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            data-testid="next-available-duration"
          />
        </label>
      ) : (
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Min. nights
          <input
            type="number"
            min={1}
            step={1}
            value={Math.max(1, Math.round(duration / (24 * 60)))}
            onChange={(e) => {
              const nights = Math.max(1, Number(e.target.value) || 1);
              setDuration(nights * 24 * 60);
            }}
            className="num rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            data-testid="next-available-duration"
          />
        </label>
      )}
      <button
        type="button"
        onClick={find}
        disabled={busy}
        className="inline-flex items-center justify-center gap-1.5 rounded-md bg-foreground px-3 py-2 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50 press"
        data-testid="next-available-search"
      >
        <Search className="size-4" strokeWidth={1.75} />
        {busy ? 'Searching…' : 'Find slot'}
      </button>
      {searchError && (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {searchError}
        </div>
      )}
      {result && (
        <div
          className="rounded-md border border-border bg-card p-3 text-sm"
          data-testid="next-available-result"
        >
          {result.found ? (
            <>
              <div className="font-medium">
                {result.resourceName ?? result.groomerName ?? 'Slot found'}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {result.startIso
                  ? new Date(result.startIso).toLocaleString('en-GB', {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })
                  : `${result.date} ${result.startTime ?? ''}`}
              </div>
              <Link
                to={`/bookings/new?from=calendar&date=${(result.startIso ?? '').slice(0, 10) || result.date}`}
                className="mt-2 inline-block text-xs font-medium underline"
              >
                Create booking →
              </Link>
            </>
          ) : (
            <span className="text-muted-foreground">No openings in the next 30 days.</span>
          )}
        </div>
      )}
      <ConflictDebugger facilityId={facilityId} />
    </div>
  );
}

/**
 * Tiny "Probe a slot" tool — exposes /check-conflicts to operators directly so
 * they can verify a proposed booking before opening the wizard. Lives in the
 * sidebar so the conflict-aware design is discoverable.
 */
function ConflictDebugger({ facilityId }: { facilityId: string }) {
  const noun = useSpaceNoun();
  const [proposalStart, setProposalStart] = useState('');
  const [proposalEnd, setProposalEnd] = useState('');
  const [runId, setRunId] = useState('');
  const [conflicts, setConflicts] = useState<CalendarConflict[] | null>(null);
  const [busy, setBusy] = useState(false);
  const lastQuery = useRef('');

  async function probe() {
    setBusy(true);
    const key = `${proposalStart}|${proposalEnd}|${runId}`;
    lastQuery.current = key;
    try {
      const res = await checkCalendarConflicts({
        bookingType: 'boarding',
        facilityId,
        startIso: new Date(proposalStart).toISOString(),
        endIso: new Date(proposalEnd).toISOString(),
        runId: runId || null,
      });
      if (lastQuery.current === key) setConflicts(res.conflicts);
    } catch {
      setConflicts([{ kind: 'invalid_input', message: 'Probe failed' }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="mt-4 rounded-md border border-dashed border-border p-3"
      data-testid="conflict-probe"
    >
      <div className="text-xs font-semibold text-muted-foreground">
        Probe a proposed boarding window
      </div>
      <div className="mt-2 grid grid-cols-1 gap-2">
        <input
          type="datetime-local"
          value={proposalStart}
          onChange={(e) => setProposalStart(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        />
        <input
          type="datetime-local"
          value={proposalEnd}
          onChange={(e) => setProposalEnd(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        />
        <input
          type="text"
          placeholder={`${noun.Singular} id (optional)`}
          value={runId}
          onChange={(e) => setRunId(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        />
        <button
          type="button"
          onClick={probe}
          disabled={busy || !proposalStart || !proposalEnd}
          className="rounded-md border border-border bg-secondary px-2 py-1.5 text-xs font-medium disabled:opacity-50 press"
        >
          Check
        </button>
      </div>
      {conflicts && (
        <div className="mt-2 space-y-1" data-testid="conflict-probe-results">
          {conflicts.length === 0 ? (
            <div className="rounded-md bg-emerald-500/10 px-2 py-1.5 text-xs text-emerald-900">
              No conflicts — slot is clear.
            </div>
          ) : (
            conflicts.map((c, i) => (
              <div
                key={`${c.kind}-${i}`}
                className="rounded-md bg-destructive/10 px-2 py-1.5 text-xs text-destructive"
              >
                <strong>{c.kind}:</strong> {c.message}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
